<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import V2MenuCategorySelect from '@/components/v2/menu-import/editor/V2MenuCategorySelect.vue';
  import V2MenuPhotoSlot from '@/components/v2/menu-import/editor/V2MenuPhotoSlot.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { useProductEditorContext } from '@/composition/catalog/use-product-editor';
  import { uploadMenuPhoto } from '@/composition/menu-photo-upload';
  import { useUi } from '@/composition/use-ui';
  import {
    DESCRIPTION_HINT_LENGTH,
    MAX_LONG_TEXT_LENGTH,
    MAX_NAME_LENGTH,
  } from '@/domain/catalog/product-form';
  import { nextDomId } from '@/utils/dom-id.utils';
  import V2CatalogEditorSection from '../shared/V2CatalogEditorSection.vue';

  /**
   * «Lo básico» (plan §5.12): photo, «Nombre», «Categoría» (same-menu
   * categories) and «Descripción (opcional)». The photo uploads right away
   * and is saved with the product.
   */
  const editor = useProductEditorContext();
  const { data } = useCatalogPage();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const nameId = nextDomId('product-name');
  const categoryLabelId = nextDomId('product-category');
  const descriptionId = nextDomId('product-description');
  const uploading = ref(false);
  let unmounted = false;
  onBeforeUnmount(() => {
    unmounted = true;
  });

  const form = computed(() => editor.form.value);
  const readOnly = computed(() => editor.readOnly.value);

  // «Crear y agregar otro»: the next product starts at its name.
  watch(editor.focusNameSignal, async () => {
    await nextTick();
    if (unmounted) return;
    const field = document.getElementById(nameId);
    if (field instanceof HTMLTextAreaElement) field.focus();
  });

  const categoryOptions = computed(() =>
    data.sections.value.map((section) => ({
      value: String(section.category.id),
      label: section.category.name,
    }))
  );

  const nameError = computed(() => {
    const error = editor.visibleErrors.value.name;
    if (error === 'required') return t('menuPage.editor.errors.name');
    if (error === 'tooLong')
      return t('menuPage.editor.errors.tooLong', { n: MAX_NAME_LENGTH });
    return undefined;
  });

  const descriptionHint = computed(() =>
    (form.value?.description.trim().length ?? 0) > DESCRIPTION_HINT_LENGTH
      ? t('menuPage.editor.descriptionHint')
      : undefined
  );

  async function uploadPhoto(file: File) {
    uploading.value = true;
    try {
      const path = await uploadMenuPhoto(file);
      if (!unmounted) editor.update('picture', path);
    } catch (error) {
      if (!unmounted) ui.serverError(error, t('menuPage.errors.photo'));
    } finally {
      if (!unmounted) uploading.value = false;
    }
  }

  /** The photo is part of the next «Guardar» until it is in the form. */
  function pickPhoto(file: File) {
    editor.trackUpload(uploadPhoto(file));
  }
</script>

<template>
  <V2CatalogEditorSection
    v-if="form"
    class="v2-catalog-basics-section"
    :title="t('menuPage.editor.basics')"
  >
    <div class="basics-top">
      <V2MenuPhotoSlot
        class="basics-photo"
        compact
        :value="form.picture"
        :uploading="uploading"
        :disabled="readOnly"
        @pick="pickPhoto"
        @remove="editor.update('picture', null)"
      />
      <div class="basics-fields">
        <PosUiField
          :label="t('menuPage.editor.name')"
          :input-id="nameId"
          :error="nameError"
        >
          <PosUiTextarea
            :id="nameId"
            :value="form.name"
            :maxlength="MAX_NAME_LENGTH"
            :auto-grow="{ minRows: 1, maxRows: 3 }"
            :disabled="readOnly"
            @input="editor.update('name', $event)"
          />
        </PosUiField>
        <PosUiField
          class="basics-category"
          :label="t('menuPage.editor.category')"
          :label-id="categoryLabelId"
        >
          <V2MenuCategorySelect
            :value="String(form.categoryId)"
            :options="categoryOptions"
            :label-id="categoryLabelId"
            :list-label="t('menuPage.editor.category')"
            :disabled="readOnly"
            @input="editor.update('categoryId', Number($event))"
          />
        </PosUiField>
      </div>
    </div>
    <PosUiField
      class="basics-description"
      :label="t('menuPage.editor.description')"
      :input-id="descriptionId"
      :hint="descriptionHint"
    >
      <PosUiTextarea
        :id="descriptionId"
        :value="form.description"
        :maxlength="MAX_LONG_TEXT_LENGTH"
        :auto-grow="{ minRows: 2, maxRows: 8 }"
        :disabled="readOnly"
        @input="editor.update('description', $event)"
      />
    </PosUiField>
  </V2CatalogEditorSection>
</template>

<style scoped lang="scss">
  .v2-catalog-basics-section {
    // Margins, not flex gap (Chrome < 84): the photo sits beside the fields
    // and wraps on top of them in narrow editors.
    .basics-top {
      @apply -tw-mx-2 tw-flex tw-flex-wrap tw-items-start;

      .basics-photo {
        @apply tw-mx-2 tw-mb-3 tw-flex-none;
      }

      .basics-fields {
        @apply tw-mx-2 tw-min-w-0 tw-flex-1;
        flex-basis: 200px;
      }
    }

    .basics-category {
      @apply tw-mt-3;
    }

    .basics-description {
      @apply tw-mt-1;
    }
  }
</style>
